import type { UseTuningSession } from '../hooks/useTuningSession';
import { Card } from '@/components/ui/Card';
import { GoalCard, TipCard, WarningCard, InfoCard } from '../components/Cards';
import { CheatSheetButton } from '../components/CheatSheets';
import { SafetyNote } from '@/components/SafetyNote';
import { SAFETY_NOTES } from '@/lib/disclaimers';

type Direction = 'same' | 'high' | 'low' | 'left' | 'right';

interface BareshaftData {
  distance?: number;
  bareshaftDirection?: Direction;

  // Extended-distance progression (v114 — Dudley's "if your bareshaft holds at
  // 70, you're tuned forever" gold standard). Optional — archer doesn't have to
  // attempt extended distances; the close-range test still tunes most rigs.
  bareshaft30Match?: 'untested' | 'same' | 'drifting';
  bareshaft50Match?: 'untested' | 'same' | 'drifting';
  bareshaft70Match?: 'untested' | 'same' | 'drifting';
}

interface Props { tuning: UseTuningSession }

/**
 * Step 8 — Bareshaft Refinement
 *
 * Shoot an unfletched arrow alongside a fletched one. Fletching masks small
 * dynamic-spine errors; the bareshaft reveals them. Same hole = great.
 * Different hole = use POI chart to fix.
 */
export function BareshaftStep({ tuning }: Props) {
  const data = (tuning.session.stepData['bareshaft'] as BareshaftData | undefined) ?? {};
  const update = (patch: Partial<BareshaftData>) =>
    tuning.updateStepData<BareshaftData>('bareshaft', { ...data, ...patch });

  // Safety note becomes more relevant when the user enables the extended
  // 50/70-yd progression — but we surface it at the top of the step always, so
  // the archer sees it before opting in.
  return (
    <>
      <SafetyNote severity={SAFETY_NOTES.bareshaft.severity} title={SAFETY_NOTES.bareshaft.title}>
        {SAFETY_NOTES.bareshaft.body}
      </SafetyNote>

      <GoalCard title="What we're trying to achieve">
        Same point of impact between a bareshaft and a fletched arrow at 15-20 yards.
        If they group together, your dynamic spine + cam timing + rest position are all
        dialed. If they don't, the bareshaft tells you exactly what's off.
      </GoalCard>

      <Card padding="lg">
        <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
          <h3 className="font-semibold">Setup</h3>
          <CheatSheetButton defaultSheet="poi-tuning" label="POI tuning guide" />
        </div>
        <ol className="list-decimal list-inside space-y-1 text-[13px] text-zinc-300 leading-relaxed">
          <li>Build one bareshaft arrow — strip the fletching but keep the nock + insert + point.</li>
          <li>
            Stand at 15-20 yards. Shoot 3 fletched arrows at the target — those mark your
            fletched group center.
          </li>
          <li>
            Shoot 3 bareshafts at the SAME aim point. Compare the bareshaft group center to
            the fletched group center.
          </li>
        </ol>

        <Field label="Distance (yards)" className="mt-4">
          <input
            type="number"
            min={10}
            max={30}
            step={1}
            value={data.distance ?? ''}
            onChange={(e) => update({ distance: parseFloatOrUndef(e.target.value) })}
            placeholder="15-20"
            className={inputClass}
          />
        </Field>
      </Card>

      <Card padding="lg">
        <h3 className="font-semibold mb-3">Where does your bareshaft group land relative to fletched?</h3>
        <div className="grid sm:grid-cols-2 gap-2">
          <Choice label="✓ Same hole — touching the fletched group" selected={data.bareshaftDirection === 'same'} onClick={() => update({ bareshaftDirection: 'same' })} />
          <Choice label="↑ Bareshaft HIGH of fletched" selected={data.bareshaftDirection === 'high'} onClick={() => update({ bareshaftDirection: 'high' })} />
          <Choice label="↓ Bareshaft LOW of fletched" selected={data.bareshaftDirection === 'low'} onClick={() => update({ bareshaftDirection: 'low' })} />
          <Choice label="← Bareshaft LEFT of fletched (RH)" selected={data.bareshaftDirection === 'left'} onClick={() => update({ bareshaftDirection: 'left' })} />
          <Choice label="→ Bareshaft RIGHT of fletched (RH)" selected={data.bareshaftDirection === 'right'} onClick={() => update({ bareshaftDirection: 'right' })} />
        </div>
      </Card>

      {data.bareshaftDirection === 'same' && (
        <Status ok>
          Excellent — dynamic tune is dialed. Move on to walk-back to confirm at distance.
        </Status>
      )}

      {data.bareshaftDirection === 'high' && (
        <TipCard title="Bareshaft HIGH — fix" defaultOpen>
          <p className="font-semibold text-zinc-100 mb-1">Solutions (try in order):</p>
          <ol className="list-decimal list-inside space-y-1 text-zinc-300">
            <li>Move the nocking point UP (raise nock-set or D-loop on the string).</li>
            <li>Move the rest DOWN.</li>
            <li>Advance the TOP cam — add twists to the top cable end.</li>
            <li>Delay the BOTTOM cam — remove twists from the bottom cable end.</li>
          </ol>
        </TipCard>
      )}

      {data.bareshaftDirection === 'low' && (
        <TipCard title="Bareshaft LOW — fix" defaultOpen>
          <p className="font-semibold text-zinc-100 mb-1">Solutions (try in order):</p>
          <ol className="list-decimal list-inside space-y-1 text-zinc-300">
            <li>Move the nocking point DOWN.</li>
            <li>Move the rest UP.</li>
            <li>Advance the BOTTOM cam — add twists to the bottom cable end.</li>
            <li>Delay the TOP cam — remove twists from the top cable end.</li>
          </ol>
        </TipCard>
      )}

      {data.bareshaftDirection === 'left' && (
        <TipCard title="Bareshaft LEFT — fix (RH)" defaultOpen>
          <p className="font-semibold text-zinc-100 mb-1">Solutions (try in order):</p>
          <ol className="list-decimal list-inside space-y-1 text-zinc-300">
            <li>Move the cam RIGHT (shim or yoke-twist toward shooter-right).</li>
            <li>Twist the RIGHT yoke — untwist the LEFT yoke (single-leg).</li>
            <li>Move the rest LEFT (toward riser).</li>
          </ol>
          <p className="text-[11px] text-zinc-400 mt-2">
            If bareshaft consistently lands LEFT even after these fixes, your spine may be
            too STIFF — common with light points and short arrows on heavy bows.
          </p>
        </TipCard>
      )}

      {data.bareshaftDirection === 'right' && (
        <TipCard title="Bareshaft RIGHT — fix (RH)" defaultOpen>
          <p className="font-semibold text-zinc-100 mb-1">Solutions (try in order):</p>
          <ol className="list-decimal list-inside space-y-1 text-zinc-300">
            <li>Move the cam LEFT (shim or yoke-twist toward shooter-left).</li>
            <li>Twist the LEFT yoke — untwist the RIGHT yoke (single-leg).</li>
            <li>Move the rest RIGHT (away from riser).</li>
          </ol>
          <p className="text-[11px] text-zinc-400 mt-2">
            If bareshaft persistently lands RIGHT, spine may be too WEAK — common with
            heavy points on long arrows.
          </p>
        </TipCard>
      )}

      {/* ──────────────────────────────────────────────────────────────────
          EXTENDED-DISTANCE PROGRESSION (v114 — School of Nock gap closure).
          Dudley's gold standard: if a bareshaft groups with a fletched arrow
          at 70 yd, you're "tuned forever" — every shorter shot inherits the
          accuracy. Optional — close-range pass is still a valid tune.
          ────────────────────────────────────────────────────────────── */}
      {data.bareshaftDirection === 'same' && (
        <Card padding="lg">
          <h3 className="font-semibold mb-3">Extended-distance progression (optional)</h3>
          <p className="text-[12px] text-zinc-400 mb-4 leading-relaxed">
            Close-range bareshaft groupings hide imperfections that only show up at
            distance. The gold standard: walk the bareshaft test out to 30, 50, then
            70 yards. If your bareshaft still groups with fletched at 70 yd, the bow
            is "tuned forever" — every shorter shot inherits the accuracy.
          </p>

          <div className="space-y-3">
            <Field label="30 yards — bareshaft still grouping with fletched?">
              <select
                value={data.bareshaft30Match ?? 'untested'}
                onChange={(e) =>
                  update({ bareshaft30Match: e.target.value as 'untested' | 'same' | 'drifting' })
                }
                className={inputClass}
              >
                <option value="untested">Not tested yet</option>
                <option value="same">Same hole · in spec</option>
                <option value="drifting">Drifting · re-tune before going further</option>
              </select>
            </Field>

            {data.bareshaft30Match === 'same' && (
              <Field label="50 yards — bareshaft still grouping?">
                <select
                  value={data.bareshaft50Match ?? 'untested'}
                  onChange={(e) =>
                    update({ bareshaft50Match: e.target.value as 'untested' | 'same' | 'drifting' })
                  }
                  className={inputClass}
                >
                  <option value="untested">Not tested yet</option>
                  <option value="same">Still grouping at 50 · serious tune</option>
                  <option value="drifting">Drifting at 50 · spine + cam fine-tune</option>
                </select>
              </Field>
            )}

            {data.bareshaft50Match === 'same' && (
              <Field label="70 yards — bareshaft still grouping?">
                <select
                  value={data.bareshaft70Match ?? 'untested'}
                  onChange={(e) =>
                    update({ bareshaft70Match: e.target.value as 'untested' | 'same' | 'drifting' })
                  }
                  className={inputClass}
                >
                  <option value="untested">Not attempted</option>
                  <option value="same">Same hole at 70 · tuned forever</option>
                  <option value="drifting">Drifting at 70 · 99th-percentile tune still ahead</option>
                </select>
              </Field>
            )}

            {data.bareshaft70Match === 'same' && (
              <Status ok>
                The "tuned forever" standard. This is the upper bar of compound-bow
                tuning — every shorter shot you take with this rig inherits this
                accuracy. Save the session and don't touch the bow.
              </Status>
            )}

            {data.bareshaft50Match === 'same' && data.bareshaft70Match !== 'same' && (
              <Status ok>
                50-yard bareshaft pass is a serious tune — well above the average
                hunter's bow. 70-yard is the next milestone if you want to chase it.
              </Status>
            )}

            {(data.bareshaft30Match === 'drifting' ||
              data.bareshaft50Match === 'drifting' ||
              data.bareshaft70Match === 'drifting') && (
              <Status>
                Drift at extended distance usually means dynamic spine is slightly
                off, or cam timing has shifted ~1/4 twist. Re-check Tiller and Cam
                Timing, then a fresh paper tune. Distance amplifies what close range
                hides.
              </Status>
            )}
          </div>
        </Card>
      )}

      <InfoCard title="Vertical first, then horizontal">
        <p>
          Vertical bareshaft mismatch (high/low) is usually nocking point or cam timing.
          Horizontal is rest position, cam lean, or spine. Fix vertical first because a
          vertical issue can mask or amplify horizontal results. After every fix, re-shoot
          the bareshaft group before judging.
        </p>
      </InfoCard>

      <WarningCard title="Safety + range considerations">
        Bareshafts can fly erratically — only extend distance once close-range groups
        match. Use a deep, large backstop, control the entire downrange area, and
        stop immediately if a bareshaft is grouping wildly ("all over" rather than
        "off in one direction") — that's a rest-contact or nock-fit issue, not a
        tune issue. The 70-yard standard assumes a private range and a confident
        archer; if you only have a public range or short backstop, the close-range
        pass is still a valid tune.
      </WarningCard>
    </>
  );
}

const inputClass =
  'w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-ember-500';

function Field({ label, children, className }: { label: string; children: React.ReactNode; className?: string }) {
  return <div className={className}><label className="text-[11px] text-zinc-500 block mb-1">{label}</label>{children}</div>;
}

function Choice({ label, selected, onClick }: { label: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={
        'text-left rounded-md border px-3 py-2.5 text-sm transition ' +
        (selected
          ? 'border-ember-500 bg-ember-500/10 text-ember-500 font-semibold'
          : 'border-zinc-800 bg-zinc-950 text-zinc-300 hover:border-zinc-700')
      }
    >
      {label}
    </button>
  );
}

function Status({ children, ok }: { children: React.ReactNode; ok?: boolean }) {
  return (
    <div className={`text-sm leading-relaxed p-2.5 rounded border ${ok ? 'border-emerald-700/40 bg-emerald-950/20 text-emerald-300' : 'border-amber-700/40 bg-amber-950/20 text-amber-300'}`}>
      {children}
    </div>
  );
}

function parseFloatOrUndef(s: string): number | undefined {
  if (s === '') return undefined;
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : undefined;
}
